(ns ui.editors.entity-creator.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entity-type-names :as etn]
   [common.schema.entry-criteria.model :as criteria-model]
   [common.schema.entry-criteria.utils :as entry-u]
   [common.schema.field :as schema-field]
   [common.schema.registry :as registry]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.select.select :as select]
   [ui.components.sortable-list.table :as sortable-table-list]
   [ui.components.tab-strip.view :as tab-strip]
   [ui.editors.entity-creator.associations :as a]
   [ui.editors.entity-creator.copy-config :as copy-config]
   [ui.editors.entity-creator.event]
   [ui.editors.entity-creator.model :as m]
   [ui.editors.entity-creator.sub]
   [ui.editors.entity-creator.utils :as u]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]))

(defn visibility-popover
  [{:keys [state close-popover! field-config
           index config-path entity-type attribute exclude-attributes]}]
  (let [visibility-state (r/cursor state [:visibility])
        stages @(rf/subscribe [m/stages])
        protected-field? (ecu/protected-creator-field? entity-type attribute)
        visibility-key (if (seq stages)
                         :stages
                         :create)
        current-visibility (u/simplified-visibility field-config
                                                    visibility-key)
        visibility @visibility-state]
    [:div
     {:class (uu/cm "w-[540px]")}
     [:div
      {:class (uu/cm "font-bold mb-1")}
      (loc/tr :creator-config/field-visibility-requirements)]
     [:div
      {:class (uu/cm "flex flex-col gap-2")}
      (if (seq stages)
        (doall
         (for [{:keys [db/id] :as stage} stages
               :let [setting (get-in @visibility-state [:stages id])
                     disabled? (u/inherited-mandatory? (get visibility visibility-key)
                                                       stages
                                                       stage)]]
           ^{:key (str id)}
           [:div
            {:class (uu/cm "flex items-center justify-between gap-3")}
            [:div (entity/display-string stage)]
            [:div
             {:class (uu/cm "w-48" (when disabled?
                                     "text-disabled-light-text cursor-not-allowed"))}
             [select/view
              {:value setting
               :placeholder (loc/tr :select)
               ;;TODO UI doesn't indicate disabled => use data-input(?)
               ;;     or add manual class-names(?)
               ;;     + add tooltip "inherited from earlier stage" etc
               :disabled? disabled?
               :options (->> (cond-> [:mandatory
                                      :optional]
                               (not protected-field?)
                               (conj :not-shown))
                             (mapv (fn [k]
                                     {:value k
                                      :label (loc/tr k)})))
               :on-change (fn [setting]
                            (u/set-and-inherit! {:state visibility-state
                                                 :stages stages
                                                 :stage stage
                                                 :new-setting setting}))}]]]))
        [:div
         {:class "w-full"}
         [:div
          (loc/tr :creator-config/setting-on-create)]
         [select/view
          {:value (:create visibility)
           :placeholder (loc/tr :select)
           ;;TODO UI doesn't indicate disabled => use data-input(?)
           ;;     or add manual class-names(?)
           ;;     + add tooltip "inherited from earlier stage" etc
           :options (->> [:mandatory
                          :optional]
                         (mapv (fn [k]
                                 {:value k
                                  :label (loc/tr k)})))
           :on-change (fn [setting]
                        (swap! state assoc-in [:visibility :create] setting))}]])]
     [:div
      {:class (uu/cm "flex justify-between mt-2")}
      [button/secondary-hollow
       {:on-click close-popover!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:on-click (fn [_]
                    (rf/dispatch [m/update-field-visibility
                                  {:index index
                                   :config-path config-path
                                   :entity-type entity-type
                                   :attribute attribute
                                   :exclude-attributes exclude-attributes
                                   :visibility visibility}])
                    (close-popover!))
        :disabled? (= visibility current-visibility)}
       (loc/tr :save)]]]))

(defn field-actions-cell
  [{:keys [config-path index attribute field-config
           entity-type stages exclude-attributes]}]
  (r/with-let [state (r/atom {})
               close-visibility! (fn []
                                   (swap! state dissoc :visibility-open?))]
    (let [{:keys [visibility-open?]} @state
          pipeline @(rf/subscribe [m/pipeline])
          protected-field? (ecu/protected-creator-field? entity-type attribute)
          mandatory-fields @(rf/subscribe [m/system-mandatory
                                           {:pipeline    pipeline
                                            :entity-type entity-type}])
          ;;name-attribute (registry/resolve-name-attribute entity-type)
          actions (cond-> []
                    ;; TODO uncomment when implemented:
                    ;; (not= name-attribute attribute)
                    ;; (conj {:label    "Define default value"
                    ;;        :on-click (fn [_]
                    ;;                    (rf/dispatch [m/edit-default-value
                    ;;                                  {:config-path config-path
                    ;;                                   :entity-type entity-type
                    ;;                                   :index       index
                    ;;                                   :exclude-attributes exclude-attributes
                    ;;                                   :attribute   attribute}]))})
                    (not (contains? mandatory-fields attribute))
                    (conj {:label    [:span
                                      (loc/tr :creator-config/field-visibility)]
                           :on-click (fn [_]
                                       (let [visibility-key (if (seq stages)
                                                              :stages
                                                              :create)
                                             visibility (u/simplified-visibility
                                                         field-config
                                                         visibility-key)]
                                         (swap! state assoc
                                                :visibility-open? true
                                                :visibility visibility)))})
                    (and (not (contains? mandatory-fields attribute))
                         (not protected-field?))
                    (conj {:label    [:span
                                      [:i
                                       {:class (uu/cm "fa fa-trash-alt"
                                                      "mr-2")}]
                                      (loc/tr :remove)]
                           :on-click (fn [_]
                                       (rf/dispatch [m/remove-attribute
                                                     {:config-path config-path
                                                      :entity-type entity-type
                                                      :index       index
                                                      :exclude-attributes exclude-attributes
                                                      :attribute   attribute}]))}))]
      [:td
       {:class (uu/cm "w-[44px]"
                      "bg-white"
                      "sticky #_right-0 z-20 w-10 px-1 py-2"
                      "lg:-right-6 -right-4")}
       [popover/view
        {:open? visibility-open?
         :modal? true
         :gray-out-background? true
         :content-class (uu/cm "overflow-auto"
                               "max-h-[var(--radix-popover-content-available-height)]")
         :direction :bottom-left
         :on-open-change (fn [val]
                           (swap! state assoc :visibility-open? val))}
        nil
        [:div
         [visibility-popover {:state state
                              :config-path config-path
                              :field-config field-config
                              :index index
                              :entity-type entity-type
                              :attribute attribute
                              :exclude-attributes exclude-attributes
                              :close-popover! close-visibility!}]]]
       (if (empty? actions)
         [:i
          {:class (uu/cm "fa fa-ellipsis-v cursor-not-allowed min-w-4 text-center"
                         "w-8 h-8 flex items-center justify-center"
                         "text-disabled-regular-text")}]
         [dropdown/view
          {:trigger
           [:i
            {:class (uu/cm "fa fa-ellipsis-v"
                           "w-8 h-8 flex justify-center items-center rounded"
                           "hover:bg-base-regular hover:text-base-light-text-paler")}]
           :items
           actions}])])))

(defn stage-config-cell-component
  [{:keys [stage-config]}]
  (let [{:keys [setting non-editable?
                system-mandatory?]} stage-config
        setting (if system-mandatory?
                  :system-mandatory
                  setting)
        disabled? non-editable?]
    [:td
     {:class (uu/cm "text-center")}
     (if disabled?
       [tooltip/view
        {:trigger-class (uu/cm "text-disabled-regular-text"
                               "cursor-default")}
        [:div
         (loc/tr (:setting stage-config))]
        (loc/tr (if system-mandatory?
                  :creator-config/always-mandatory
                  :creator-config/inherited-setting))]
       [:div
        (loc/tr setting)])]))

(defn update-config!
  [ctx new-setting]
  (rf/dispatch
   [m/update-field-config
    (assoc ctx
           :setting new-setting)]))

(defn stages-config-cells
  [{:keys [index field-config config-path
           entity-type dropdown-container exclude-attributes]}]
  (let [stages @(rf/subscribe [m/stages])
        #_#_update-config! (fn [{:keys [index stage-id]} new-setting]
                             (rf/dispatch
                              [m/update-field-config
                               {:setting new-setting
                                :index   index}]))
        stages-config @(rf/subscribe [m/stages-config
                                      {:entity-type  entity-type
                                       :field-config field-config}])]
    [:<>
     (doall
      (for [{:keys [db/id] :as stage} stages
            :let [stage-config (get stages-config id)]]
        ^{:key (str "stage-config-" id)}
        [stage-config-cell-component
         {:dropdown-container dropdown-container
          :update-config!     (r/partial update-config!
                                         {:index        index
                                          :config-path  config-path
                                          :exclude-attributes exclude-attributes
                                          :field-config field-config
                                          :stage-id     id
                                          :stage-config stage-config})
          :stage              stage
          :stage-config       stage-config}]))]))

(defn create-config-cell
  [{:keys [_index field-config _config-path entity-type _dropdown-container]}]
  (let [config @(rf/subscribe [m/create-config
                               {:entity-type  entity-type
                                :field-config field-config}])
        {:keys [setting non-editable?
                system-mandatory?]} config
        _setting (if system-mandatory?
                   :system-mandatory
                   setting)
        disabled? non-editable?]
    [:td
     {:class (uu/cm "text-center")}
     (if disabled?
       [tooltip/view
        {:trigger-class (uu/cm "text-disabled-regular-text")}
        [:div
         (loc/tr (:setting config))]
        [:div (loc/tr :creator-config/always-mandatory)]]
       [:div (loc/tr (:setting config))])]))

(defn team-access-warning
  [{:keys [entity-type]} teams]
  (let [team-name (fn [id]
                    (:team/name @(rf/subscribe [::session-sub/entity id])))
        pipeline @(rf/subscribe [m/pipeline])
        infos-str (fn [infos]
                    (when (seq infos)
                      (loc/tr :wrap-parens
                              [(->> infos
                                    (mapv loc/tr)
                                    (string/join ", "))])))
        etn (etn/get-plural entity-type :en)
        teams-str (->> teams
                       (mapv (fn [[id infos]]
                               (str (team-name id)
                                    " "
                                    (infos-str infos))))
                       (string/join ", "))]
    [tooltip/view
     {:side          "right"
      :content-class (uu/cm "bg-secondary-gloomy")}
     [:span
      [:i
       {:class (uu/cm "fal fa-exclamation-circle pl-2")}]]
     [:div
      {:class (uu/cm "flex flex-col gap-2")}
      [:div
       (loc/tr (if pipeline
                 :creator-config/team-access-warning-1
                 :creator-config/team-access-warning-1-no-pipeline)
               [etn])]
      [:div
       (loc/tr :creator-config/team-access-warning-2)]
      [:div
       (loc/tr :creator-config/team-access-warning-3 [teams-str])]]]))

(defn inactive-field-warning
  [inactive-field]
  [tooltip/view
   {:side          "right"
    :content-class (uu/cm "bg-secondary-gloomy")}
   [:span
    [:i
     {:class (uu/cm "fal fa-exclamation-circle pl-2")}]]
   [:div
    {:class (uu/cm "flex flex-col gap-2")}
    [:div
     (case inactive-field
       :blacklisted
       [:div
        (loc/tr :creator-config/blacklisted-field-warning)]

       :inactive
       [:div
        (loc/tr :creator-config/inactive-field-warning)]

       :inactive-for-pipeline
       [:div
        (loc/tr :creator-config/inactive-pipeline-field-warning)]

       nil)]]])

(defn field-type
  [{:keys [field-schema attribute]}]
  (let [{:field/keys [data-type]} field-schema]
    (cond
      (schema-field/tag-field? attribute)
      (loc/tr :tag)
      (= :common/attachments attribute)
      (loc/tr :file)
      (and (= :data.type/ref-single
              data-type)
           (:named-value-attribute field-schema))
      (loc/tr :option-single)
      (and (= :data.type/ref-single
              data-type)
           (:named-value-attribute field-schema))
      (loc/tr :option-multi)
      :else
      (->> data-type
           (get data-types/zapflow-data-types)
           :translations
           :en))))

(defn field-table-row-item
  [_]
  (let [ref (atom nil)
        open? (r/atom false)]
    (fn [{:keys [field-config config-path entity-type index
                 exclude-attributes _is-dragging?]}]
      (let [{:keys [attribute edit-default?]} field-config
            fs (registry/get-field-schema @session/tenant-schema attribute)
            inactive-field @(rf/subscribe [m/inactive-field
                                           {:entity-type entity-type
                                            :attribute   attribute}])
            teams @(rf/subscribe [m/teams-without-write-access
                                  {:entity-type entity-type
                                   :attribute   attribute}])
            stages @(rf/subscribe [m/stages])]
        [:<>
         [:td
          {:class
           (uu/cm "cursor-move"
                  "bg-white"
                  "sticky z-20 px-1"
                  "lg:-left-6 -left-4"
                  "w-6 min-w-6"
                  "lg:w-8 lg:min-w-8")}
          [:i
           {:class (uu/cm "fal fa-grip-vertical"
                          "w-full" "ml-2")}]]
         [:td
          {:class
           (uu/cm "justify-between"
                  "items-center"
                  "pl-8"
                  "sticky left-2 z-20 px-4"
                  "min-w-64"
                  "bg-white"
                  "border-r")}
          [:div
           {:class "flex py-3 items-center"}
           [:div
            {:class
             (uu/cm "h-full" "flex" "items-center")}
            [:i
             {:class "fa fa-input-pipe pr-3"}]]
           [:div
            {:class (uu/cm "flex flex-col")}
            [:div
             {:class (uu/cm "font-bold")}
             (loc/field-name attribute fs)]
            [:div
             {:class (uu/cm "text-sm mb-0 text-base-light-text-paler")}
             " "
             [field-type {:field-schema fs
                          :attribute    attribute}]]]
           (when (seq teams)
             [team-access-warning {:entity-type entity-type} teams])
           (when inactive-field
             [inactive-field-warning inactive-field])
           (when edit-default?
             [:div "default-value-editor"])]]
         (if (seq stages)
           [stages-config-cells
            {:field-config       field-config
             :config-path        config-path
             :entity-type        entity-type
             :index              index
             :exclude-attributes exclude-attributes
             :dropdown-container ref}]
           [create-config-cell
            {:field-config       field-config
             :config-path        config-path
             :entity-type        entity-type
             :index              index
             :dropdown-container ref}])
         [field-actions-cell
          {:field-config       field-config
           :config-path        config-path
           :dropdown-container ref
           :index              index
           :exclude-attributes exclude-attributes
           :attribute          attribute
           :entity-type        entity-type
           :open?              open?
           :stages stages}]]))))

(defn sortable-table-fields
  [{:keys [entity-type config-path exclude-attributes]}]
  (let [config @(rf/subscribe [m/edited-config {:config-path config-path}])
        reorder-fields (fn [from to]
                         (rf/dispatch-sync
                          [m/reorder-fields
                           {:from        from :to to
                            :config-path config-path
                            :entity-type entity-type
                            :exclude-attributes exclude-attributes}]))
        fields (:fields config)
        fields (if (seq exclude-attributes)
                 (filterv (fn [{:keys [attribute]}]
                            (not (contains? exclude-attributes attribute)))
                          fields)
                 fields)]
    [sortable-table-list/view
     {:items           (vec fields)
      :gap             0
      :get-key         (fn [item]
                         (-> item :attribute str))
      :on-reorder      (fn [{:keys [source-index destination-index]}]
                         (reorder-fields source-index destination-index))
      :tr-class (uu/cm "border rounded")
      :render-item     (fn [{:keys [item index dragging?]}]
                         [field-table-row-item
                          {:field-config item
                           :is-dragging? dragging?
                           :index        index
                           :config-path  config-path
                           :entity-type  entity-type
                           :exclude-attributes exclude-attributes}])}]))

(defn fields-table-header
  [{:keys [target?]}]
  (let [pipeline @(rf/subscribe [m/pipeline])
        stages @(rf/subscribe [m/stages])
        pipeline-config (:pipeline/creator-config pipeline)
        prevent-create-config (when (and pipeline (seq stages))
                                (->> (ecu/resolve-prevent-stage-config
                                      {:config pipeline-config
                                       :stages stages})
                                     :prevent-create?
                                     :stages))]
    [:tr
     {:class
      (uu/cm "bg-base-regular rounded border")}
     [:th
      {:class (uu/cm "sticky left-0 z-20 mx-1 py-2 bg-base-regular"
                     "lg:-left-6 -left-4"
                     ;;"w-14 min-w-14"
                     ;;"lg:w-16 lg:min-w-16"
                     ;;"w-10 min-w-10"
                     ;;"lg:w-12 lg:min-w-12"
                     "w-6 min-w-6"
                     "lg:w-8 lg:min-w-8")}
      ""]
     [:th
      {:class (uu/cm "pl-8 py-2"
                     "sticky left-2 z-20 mx-4 py-2 bg-base-regular")}
      (loc/tr :creator-config/field)]
     (if (seq stages)
       (let [last-stage-id (->> stages (sort-by :common/ord) last :db/id)]
         (->> stages
              (map (fn [{:keys [db/id] :as stage}]
                     (let [stage-name (entity/display-string stage)
                           entry (get prevent-create-config id)
                           prevent-create? (= (:setting entry) :prevent)
                           non-editable? (:non-editable? entry)
                           inherited? (:inherited? entry)]
                       ^{:key (str "stage-" id)}
                       [:th
                        {:class (uu/cm
                                 "w-32 max-w-32"
                                 "items-center"
                                 "text-center"
                                 "text-ellipsis truncate _overflow-hidden whitespace-nowrap"
                                 "px-4"
                                 #_"min-w-[100px]"
                                 #_"wrap-break-word break-all"
                                 "h-full"
                                 "cursor-default"
                                 "even:bg-complement-regular")}
                        [:div
                         {:class (uu/cm "flex flex-col gap-2")}
                         (when-not target?
                           [tooltip/view
                            {:trigger-class (uu/cm
                                             (if non-editable?
                                               "cursor-not-allowed"
                                               "cursor-pointer"))}
                            [:i
                             {:class (uu/cm "fal"
                                            (if prevent-create?
                                              "fa-lock"
                                              "fa-lock-open")
                                            (if non-editable?
                                              "text-disabled-regular-text"
                                              "hover:text-base-light-text-paler"))
                              :on-click (fn [_]
                                          (when-not non-editable?
                                            (rf/dispatch [m/toggle-prevent-create
                                                          {:stage-id id}])))}]
                            (cond
                              (and non-editable? (not inherited?))
                              (loc/tr :stage-config/first-stage-allow)

                              inherited?
                              (loc/tr :stage-config/not-allowed-inherited)

                              prevent-create?
                              (loc/tr :stage-config/click-to-allow)

                              (= id last-stage-id)
                              (loc/tr :stage-config/click-to-prevent-last)

                              :else
                              (loc/tr :stage-config/click-to-prevent))])
                         [tooltip/view
                          {:trigger-class (uu/cm
                                           "truncate pb-2"
                                           "max-w-full w-full"
                                           "cursor-default")
                           :content-class "bg-secondary-gloomy"
                           :delay-duration 100
                           :side "bottom"}
                          stage-name
                          stage-name]]])))))
       [:th
        {:title (loc/tr :entity-create-config/single-stage-entity-create)
         :class (uu/cm "flex-1 flex-grow flex justify-center truncate")}
        (loc/tr :entity-create-config/single-stage-entity-create)])
     [:th
      {:class (uu/cm "w-[44px]"
                     "sticky right-0 z-20 _w-10 _px-1 py-2"
                     "lg:-right-6 -right-4"
                     "bg-base-regular")}
      ""]]))

(defn field-popover
  [{:keys [state entity-type config-path close-popover!
           exclude-attributes]}]
  (let [stages @(rf/subscribe [m/stages])
        stages? (seq stages)
        config @(rf/subscribe [m/edited-config {:config-path config-path}])
        fields (:fields config)
        current-fields-set (->> fields
                                (mapv :attribute)
                                (into #{}))
        selected-field (:field @state)
        stages-config (:stages @state)
        pipeline @(rf/subscribe [m/pipeline])
        available-fields @(rf/subscribe [m/available-fields
                                         {:entity-type entity-type
                                          :pipeline    pipeline
                                          :exclude-attributes exclude-attributes}])
        static-fields-set (get-in ecu/static-fields [entity-type :set])
        field-options (->> available-fields
                           (filterv (fn [[f _]]
                                      (not (contains? current-fields-set f))))
                           (filterv (fn [[f _]]
                                      (not (contains? static-fields-set f))))
                           (mapv (fn [[field fs]]
                                   {:label (loc/field-name field fs)
                                    :value field}))
                           (sort-by :label))]
    [:div
     {:class (uu/cm "w-[540px]")}
     [:div
      {:class (uu/cm "font-bold mb-1")}
      (loc/tr :creator-config/add-new-field)]
     [:div
      [:div
       {:class (uu/cm "mb-1")}
       (loc/tr :creator-config/select-field)]
      [select/view
       {:value selected-field
        :placeholder (loc/tr :creator-config/select-from-the-list)
        :options field-options
        :on-change (fn [field]
                     (swap! state assoc :field field))}]]
     [:div
      [:div
       {:class (uu/cm "font-bold my-2")}
       (if stages?
         (loc/tr :creator-config/stage-requirements)
         (loc/tr :creator-config/visibility-requirement))]
      [:div
       {:class (uu/cm "flex flex-col gap-2")}
       (if (seq stages)
         (doall
          (for [{:keys [db/id] :as stage} stages
                :let [setting (get-in @state [:stages id])
                      disabled? (u/inherited-mandatory? stages-config stages stage)]]
            ^{:key (str id)}
            [:div
             {:class "w-full flex items-center justify-between gap-3"}
             [:div
              {:class (uu/cm "my-1")}
              (entity/display-string stage)]
             [:div
              {:class (uu/cm "w-48"
                             (when disabled?
                               "text-disabled-light-text cursor-not-allowed"))}
              [select/view
               {:value setting
                :placeholder (loc/tr :select)
                ;;TODO UI doesn't indicate disabled => use data-input(?)
                ;;     or add manual class-names(?)
                ;;     + add tooltip "inherited from earlier stage" etc
                :disabled? disabled?
                :options (->> [:mandatory
                               :optional
                               :not-shown]
                              (mapv (fn [k]
                                      {:value k
                                       :label (loc/tr k)})))
                :on-change (fn [setting]
                             (u/set-and-inherit! {:state state
                                                  :stages stages
                                                  :stage stage
                                                  :new-setting setting}))}]]]))
         [:div
          {:class "w-full"}
          [:div]
          [select/view
           {:value (:setting @state)
            :placeholder (loc/tr :select)
           ;;TODO UI doesn't indicate disabled => use data-input(?)
           ;;     or add manual class-names(?)
           ;;     + add tooltip "inherited from earlier stage" etc
            :options (->> [:mandatory
                           :optional]
                          (mapv (fn [k]
                                  {:value k
                                   :label (loc/tr k)})))
            :on-change (fn [setting]
                         (swap! state assoc :setting setting))}]])]]
     [:div
      {:class (uu/cm "flex justify-between mt-2")}
      [button/secondary-hollow
       {:on-click close-popover!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:on-click (fn [_]
                    (rf/dispatch [m/add-attribute
                                  {:attribute selected-field
                                   :config-path config-path
                                   :entity-type entity-type
                                   :create-config (:setting @state)
                                   :stages-config stages-config}])
                    (close-popover!))
        :disabled? (not selected-field)}
       (loc/tr :add-field)]]]))

(defn init-stages!
  [{:keys [state stages]}]
  (let [stage-values (->> stages
                          (mapv :db/id)
                          (mapv (fn [id]
                                  [id :optional]))
                          (into {}))]
    (reset! state {:stages stage-values
                   :open? true})))

(defn entity-config-table
  [{:keys [entity-type config-path exclude-attributes] :as props}]
  (r/with-let [state (r/atom {})
               close-popover! (fn [_] (swap! state dissoc :open?))]
    (let [stages @(rf/subscribe [m/stages])
          pipeline @(rf/subscribe [m/pipeline])
          target-type @(rf/subscribe [m/target-type])
          config @(rf/subscribe [m/edited-config {:config-path config-path}])
          fields (:fields config)
          fields (if (seq exclude-attributes)
                   (filterv (fn [{:keys [attribute]}]
                              (not (contains? exclude-attributes attribute)))
                            fields)
                   fields)
          open? (:open? @state)]
      [:div
       (when (= entity-type :entity.type/person)
         [:<>
          [:div
           {:class (uu/cm "flex items-start p-4 rounded shadow-md"
                          "bg-blue-50 text-blue-800 border-blue-400"
                          "border-l-4 mb-4")}
           [:div
            (loc/tr :creator-config/contact-info-note)
            [:ul
             {:class (uu/cm "list-disc ml-5")}
             [:li
              (loc/field-name :person/first-name nil)]
             [:li
              (loc/field-name :person/last-name nil)]
             [:li
              (loc/field-name :person/universal-email-address nil)]]]]

          [:h2
           {:class (uu/cm "mb-2 text-xl")}
           (loc/tr :creator-config/additional-fields-title)]])

       [:div
        {:class (uu/cm "flex justify-end items-center gap-2 mt-4")}
        (when pipeline
          [copy-config/view
           {:pipeline pipeline
            :target-type target-type
            :config-path config-path}])
        [popover/view
         {:open? open?
          :modal? true
          :gray-out-background? true
          :content-class (uu/cm "overflow-auto"
                                "max-h-[var(--radix-popover-content-available-height)]")
          :direction :bottom-left
          :on-open-change (fn [val]
                            (when val
                              (if (seq stages)
                                (init-stages! {:state state
                                               :stages stages})
                                (swap! state assoc :setting :optional)))

                            (swap! state assoc :open? val))}
         [button/primary-solid
          {:on-click (fn [_]
                       ;;(swap! state assoc :open? true)
                       )}
          [:i
           {:class (uu/cm "fa fa-input-pipe")}]
          (loc/tr :creator-config/add-field)
          [:i
           {:class (uu/cm "fa fa-chevron-down")}]]
         [:div
          [field-popover {:state state
                          :entity-type entity-type
                          :config-path config-path
                          :close-popover! close-popover!
                          :exclude-attributes exclude-attributes}]]]]

       (if (seq fields)
         [:div
          [:table
           {:class (uu/cm "mt-4"
                          "min-w-full table-fixed")}
           [:thead
            [fields-table-header props]]
           [sortable-table-fields
            props]]]
         [:div
          {:class (uu/cm "m-8")}
          [:div
           {:class (uu/cm "flex items-start p-4 rounded shadow-md"
                          "bg-blue-50 text-blue-800 border-blue-400"
                          "border-l-4 mb-4")}
           [:i
            {:class "fas fa-info-circle mr-4"}]
           (loc/tr :creator-config/no-fields)]])])))

(defn pipeline-specific-info
  [target-type entity-type]
  (let [tt-name (etn/get-name target-type :en)
        et-name (etn/get-name entity-type :en)]
    [tooltip/view
     {:side          "right"
      :content-class (uu/cm "bg-secondary-gloomy")}
     [:span
      [:i
       {:class (uu/cm "fal fa-info-circle pl-2")}]]
     [:div
      {:class (uu/cm "flex flex-col gap-2")}
      [:div
       (loc/tr :creator-config/pipeline-specific-target-info
               [tt-name et-name tt-name])]]]))

(defn entry-criteria-mode-options
  "The options of the mode select. The modes and their labels come from
   `common.schema.entry-criteria.model`, which the backend validates against."
  []
  (->> criteria-model/entry-criteria-modes
       (mapv (fn [mode]
               {:value mode
                :label (loc/tr (get criteria-model/entry-criteria-mode-label-keys mode))}))))

(defn entry-criteria-mode-select
  "Sets when the entry-criteria popup opens on a stage move, for this pipeline
   and this target type. The setting lives in the main config at `config-path`
   and applies to every tab of this modal. It matters only where the stage
   entry criteria are switched on for the pipeline type."
  [{:keys [config-path]}]
  (let [config @(rf/subscribe [m/edited-config {:config-path config-path}])
        mode (entry-u/entry-criteria-mode config)]
    [:div
     {:class (uu/cm "flex flex-col gap-1 mb-4")}
     [:div
      {:class (uu/cm "font-bold")}
      (loc/tr :creator-config/entry-criteria-mode)]
     [:div
      {:class (uu/cm "text-sm")}
      (loc/tr :creator-config/entry-criteria-mode-info)]
     [:div
      {:class (uu/cm "w-[480px] max-w-full")}
      [select/view
       {:value mode
        :options (entry-criteria-mode-options)
        :on-change (fn [new-mode]
                     (rf/dispatch [m/set-entry-criteria-mode
                                   {:config-path config-path
                                    :mode new-mode}]))}]]]))

(defn opportunity-config-tabs
  [{:keys [_entity-type target-type target-profile-type]}]
  (->> [{:value :entity-config
         :label (loc/tr :opportunity)}
        (when target-type
          {:value :target-config
           :label (etn/get-name target-type :en)})
        (when target-profile-type
          {:value :target-profile-config
           :label (loc/tr :creator-config/investor-profile)})
        {:value :person-associations-config
         :label [:div (loc/tr :creator-config/people)
                 [tooltip/view
                  {}
                  [:i.fal.fa-info-circle]
                  (loc/tr :creator-config/people-info)]]}]
       (filterv some?)))

(defn opportunity-config
  []
  (r/with-let [local-state (r/atom {})
               ;;for dev convenience:
               ;;_ (swap! local-state assoc :tab :target-profile-config)
               ]
    (let [tab (:tab @local-state)
          target-type @(rf/subscribe [m/target-type])
          entity-type @(rf/subscribe [m/entity-type])
          pipeline @(rf/subscribe [m/pipeline])
          target-profile-type (ecu/resolve-target-profile-type
                               {:pipeline pipeline
                                :entity-type entity-type
                                :target-type target-type})
          pipeline @(rf/subscribe [m/pipeline])
          {:keys [main-config-path
                  target-profile-config-path
                  target-config-path]} (ecu/resolve-config-paths
                                        {:entity-type @(rf/subscribe [m/entity-type])
                                         :target-type @(rf/subscribe [m/target-type])
                                         :pipeline @(rf/subscribe [m/pipeline])})
          target-profile-exclude-attributes #{:investor/name
                                              :investor/legal-entity}
          entity-config-path main-config-path]
      [:div
       [:div
        {:class (uu/cm "flex items-start p-4 rounded shadow-md bg-blue-50 text-blue-800 border-blue-400 border-l-4 mb-4")}
        (let [entity-qualifier (case entity-type
                                 :entity.type/fundraising-opportunity (loc/tr :fundraising-lc)
                                 :entity.type/sales-opportunity (loc/tr :sales-lc)
                                 :entity.type/exit-opportunity (loc/tr :exit-lc)
                                 :entity.type/oppo (loc/tr :investment-lc)
                                 "")]
          (if target-type
            (loc/tr :creator-config/opportunity-infobox
                    [(-> target-type
                         (etn/get-plural :en)
                         (string/lower-case))
                     entity-qualifier])
            (loc/tr :creator-config/opportunity-infobox-no-target
                    [entity-qualifier])))]
       (when (session/entry-criteria-feature?)
         [entry-criteria-mode-select {:config-path entity-config-path}])
       [:div
        {:class (uu/cm "mb-2")}
        [tab-strip/view
         {:default-tab :entity-config
          :selected-tab tab
          :tabs (opportunity-config-tabs {:entity-type entity-type
                                          :target-type target-type
                                          :target-profile-type target-profile-type})
          :on-change! (r/partial swap! local-state assoc :tab)}]]
       (case tab
         :target-config
         [:div
          [entity-config-table
           {:entity-type target-type
            :config-path target-config-path
            :target? true
            :pipeline pipeline}]]

         :target-profile-config
         [:div
          [entity-config-table
           {:entity-type :entity.type/investor
            :config-path target-profile-config-path
            :exclude-attributes target-profile-exclude-attributes
            :target? true
            :pipeline pipeline}]]

         :person-associations-config
         [a/person-associations-config]

         ;;default main-entity-config:
         [:div
          [entity-config-table
           {:entity-type entity-type
            :config-path entity-config-path
            :pipeline    pipeline}]])])))

(defn view
  []
  (let [target-type @(rf/subscribe [m/target-type])
        entity-type @(rf/subscribe [m/entity-type])
        pipeline @(rf/subscribe [m/pipeline])
        {:keys [main-config-path]} (ecu/resolve-config-paths
                                    {:entity-type @(rf/subscribe [m/entity-type])
                                     :target-type @(rf/subscribe [m/target-type])
                                     :pipeline @(rf/subscribe [m/pipeline])})
        entity-config-path main-config-path]
    (cond (not @(rf/subscribe [m/open?]))
          [:div "loading"]

          ;; deal-pipeline, sales-pipeline ('target-asset' being customer),
          ;; froppo-pipeline - target-attr being
          ;;  [:fundraising-opportunity/investor :investor/legal-entity]
          ;; target can be nil if allowed in pipeline settings
          (and entity-type pipeline)
          [opportunity-config]

          ;; holding + target-asset, no pipeline
          ;;TODO company +
          (and target-type entity-type)
          [:div
           [:div
            "tenantconfig-for " entity-type
            ;;config from tenant-attribute
            ;;config-path entity-config-path;;[target-type entity-type]
            ]
           [:div
            "tenantconfig-for " target-type
            ;;config from tenant-attribute
            ;;config-path target-config-path
            ;;NOTE ^ [target-type] is insufficient - reserved for
            ;;       without holding case ("others - no pipeline" below)
            ]]

          (and entity-type pipeline)
          [:div
           "pipelineconfig-for" entity-type
           ;;config from pipeline-attribute
           ;;config-path [entity-type]
           ]

          ;; others - no pipeline
          entity-type
          [:div
           ;;"tenantconfig-for" entity-type

           ;;config from tenant-attribute
           ;;config-path entity-config-path;;[:entity-types entity-type]
           [entity-config-table
            {:entity-type entity-type
             :config-path entity-config-path
             :exclude-attributes (when (= entity-type :entity.type/investor)
                                   #{:investor/name :investor/legal-entity})}]]

          :else
          [:div "Something went wrong"])))

;;TODO just the rf-event enough?
(defn open-config!
  [{:keys [entity-type pipeline target-type]}]
  (rf/dispatch [m/open-config {:entity-type entity-type
                               :pipeline    pipeline
                               :target-type target-type}]))

(def legend-keys
  [:system-mandatory
   :not-shown
   :optional
   :mandatory
   #_:mandatory-inherited
   ;; TODO uncomment when implemented:
   #_:use-default-locked
   #_:use-default-hidden])

#_(defn legend
    []
    [:div
     {:class "flex gap-4 pb-3"}
     (doall
      (for [icon-key legend-keys
            :let [{:keys [icon label]} (get m/config-defs icon-key)]]
        ^{:key (str "icon-" icon-key)}
        [:div
         [:i
          {:class (uu/cm "fa" icon)}]
         " - " label]))])

(defn buttons
  []
  [:div
   {:class (uu/cm "flex justify-between")}
   [button/secondary-hollow
    {:on-click #(rf/dispatch [m/close-config])}
    (loc/tr :cancel)]
   (let [disabled? (not @(rf/subscribe [m/changes?]))]
     [button/primary-solid
      {:disabled? disabled?
       :tooltip   (when disabled?
                    (loc/tr :no-changes-made))
       :on-click  #(rf/dispatch [m/save-config])}
      (loc/tr :save-changes)])])

(defn footer
  []
  [:div
   {:class (uu/cm "flex flex-col w-full")}
   [buttons]])

(defn modal-view
  []
  [modal/view
   {:visible?      @(rf/subscribe [m/open?])
    :hide!         (fn []
                     (rf/dispatch [m/close-config]))
    :content-class (uu/cm "w-9/12 h-[90vh]")}
   @(rf/subscribe [m/title])
   [view]
   [footer]])
